<script lang="ts" setup>
import { computed, ref, toRefs, onMounted } from "vue";
import { onClickOutside } from "@vueuse/core";

import { useModelWrapper } from "~/composables/model-wrapper";
import ChevronDown from "~/assets/icons/chevron-down.svg";
import AttentionIcon from "~/assets/icons/attention.svg";

type ObjectOption = Record<string, any>;
type Option = ObjectOption | string | null;
type IconType = "attention";
type SelectType = "default" | "type-underline";

const iconsObject: Record<IconType, string> = {
  attention: AttentionIcon,
};

const props = defineProps<{
  label?: string;
  placeholder?: string;
  modelValue?: Option;
  options: Option[];
  labelGetter?: (option: any) => string | undefined;
  noteGetter?: (option: any) => string | undefined;
  currentNoteGetter?: (option: any) => string | undefined;
  valueGetter?: (option: any) => any;
  iconGetter?: (option: any) => IconType | "";
  postfixGetter?: (option: any) => string;
  error?: string;
  hideError?: boolean;
  hasSearch?: boolean;
  hideOptions?: boolean;
  type?: SelectType;
}>();
const emit = defineEmits(["update:modelValue", "select"]);

const currentValue = useModelWrapper(props, emit);
const {
  labelGetter,
  valueGetter,
  noteGetter,
  currentNoteGetter,
  iconGetter,
  postfixGetter,
} = props;

const { label, options } = toRefs(props);
const searchInput = ref("");
const dropVisible = ref(false);
const selectElement = ref();
const list = ref();
const append = ref();
const searchRef = ref<HTMLInputElement>();

const getValue = (option: Option) =>
  valueGetter ? valueGetter(option) : option;

const getIcon = (option: Option) => {
  if (!iconGetter) {
    return "";
  }

  const val = iconGetter(option);

  if (!val) {
    return "";
  }

  return iconsObject[val];
};

const getLabel = (option?: Option) => {
  if (!option) {
    return undefined;
  }

  if (labelGetter) {
    return labelGetter(option);
  }

  const objectOption = option as ObjectOption;

  if (objectOption.label) {
    return objectOption.label;
  }

  return option;
};

const getNote = (option?: Option) => {
  if (!option) {
    return undefined;
  }

  if (noteGetter) {
    return noteGetter(option);
  }

  const objectOption = option as ObjectOption;

  if (objectOption.note) {
    return objectOption.note;
  }

  return undefined;
};

const getCurrentNote = (option?: Option) => {
  if (!option) {
    return undefined;
  }

  if (currentNoteGetter) {
    return currentNoteGetter(option);
  }

  return getNote(option);
};

const getPostfix = (option?: Option) => {
  if (!option) {
    return undefined;
  }

  if (!postfixGetter) {
    return undefined;
  }

  return postfixGetter(option);
};

const select = (option: Option) => {
  currentValue.value = getValue(option);
  searchInput.value = getLabel(option);
  emit("select", option);
};

const dropToggle = () => {
  dropVisible.value = !dropVisible.value;
};

const onSearchCLick = () => {
  if (!dropVisible.value) dropVisible.value = true;
};

const currentOption = computed(() => {
  const result = options.value.find((o) => getValue(o) == currentValue.value);
  return result;
});
const searchOptions = computed(() => {
  if (!props.hasSearch) return options.value;

  return options.value.filter((option) => {
    const searchOption = searchInput.value.toLowerCase();
    const currentOption = getLabel(option)?.toLowerCase();

    return currentOption?.startsWith(searchOption);
  });
});

onClickOutside(selectElement, () => {
  if (props.hasSearch) {
    if (getLabel(currentOption.value)) {
      searchInput.value = getLabel(currentOption.value);
    } else {
      searchInput.value = "";
    }
  }
  dropVisible.value = false;
});
onMounted(() => {
  if (props.hasSearch && getLabel(currentOption.value))
    searchInput.value = getLabel(currentOption.value);
});
</script>

<template>
  <BaseField
    ref="selectElement"
    :label="label"
    :class="['base-select', props.type]"
    :error="error"
    :with-focus="false"
    @click="dropToggle"
  >
    <div :class="['current-option', { selected: getLabel(currentOption) }]">
      <input
        v-if="hasSearch"
        v-model="searchInput"
        ref="searchRef"
        type="text"
        class="search-input"
        :placeholder="placeholder"
        @click.stop="onSearchCLick"
      />

      <div v-else-if="getLabel(currentOption)" class="current-option__text">
        <span>
          {{ getLabel(currentOption) }}
        </span>
      </div>

      <div v-else class="current-option__placeholder">
        <span>
          {{ placeholder }}
        </span>
      </div>
      <ChevronDown :class="{ 'chevron-active': dropVisible }" />
    </div>

    <div
      class="drop"
      :class="{ 'drop-active': dropVisible }"
      v-if="!hideOptions"
    >
      <div class="drop-down">
        <ul class="drop-list" ref="list">
          <template v-for="option in searchOptions" :key="getValue(option)">
            <li
              v-if="getLabel(option)"
              @click="select(option)"
              :class="['option', { postfix: getPostfix(option) }]"
            >
              <div class="option-text">
                <span class="prepend_icon" v-if="getIcon(option)">
                  <component :is="getIcon(option)"></component>
                </span>
                <span>
                  {{ getLabel(option) }}
                </span>
              </div>

              <p
                v-if="getPostfix(option)"
                :title="getPostfix(option)"
                class="postfix-text"
              >
                {{ getPostfix(option) }}
              </p>
              <div class="note" v-if="getNote(option)">
                {{ getNote(option) }}
              </div>
            </li>
          </template>
        </ul>

        <div v-if="$slots.append" ref="append" class="drop__append">
          <slot name="append" />
        </div>
      </div>
    </div>

    <template #note>
      <span class="note">
        {{ getCurrentNote(currentOption) }}
      </span>
    </template>
  </BaseField>
</template>

<style lang="scss" scoped>
.type-underline {
  border: none;

  .current-option {
    width: auto;
    grid-column-gap: 12px;

    &__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 150px;
    }

    span {
      border-bottom: solid 1px $ui-accent;
      color: $ui-accent;
    }

    :deep(svg) {
      margin-left: 0;

      path {
        fill: $ui-accent;
      }
    }
  }

  .selected {
    span {
      color: $typography-secondary-color;
      border-bottom-color: $typography-secondary-color;
    }

    :deep(svg) {
      path {
        fill: $typography-secondary-color;
      }
    }
  }
}

.base-select {
  cursor: pointer;
}
.drop {
  position: absolute;
  visibility: hidden;
  opacity: 0;
  min-width: 100%;
  top: calc(100% + 1px);
  left: 0;
  z-index: 1000;
  padding-bottom: 10px;

  &-active {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.3s ease;
    z-index: $zindex-dropdown;
    overflow: visible;
    width: 100%;
  }

  &-down {
    background: $ui-white;
    border: 1px solid $outline-box;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.14), 0px 3px 4px rgba(0, 0, 0, 0.12),
      0px 1px 5px rgba(0, 0, 0, 0.2);
    border-radius: 4px;

    .search {
      display: flex;

      :deep(.base-field) {
        flex: 1;
      }

      input {
        width: 100%;
        outline: none;
        border: none;
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: $typography-paragraph;
      }
    }
  }

  &__append {
    margin-top: 10px;
    border-top: $outline-border-default;
    padding: 14px 16px;
    text-align: left;
  }
}

.drop-list {
  max-height: 200px;
  overflow: auto;
  padding: 0;
  margin: 0;
  z-index: 1000;
  color: $typography-paragraph;
  list-style-type: none;
}

.option {
  @include column(6px);
  padding: 14px 16px;
  @include typography-paragraph;
  text-align: left;

  color: $typography-paragraph;

  cursor: pointer;
  &:hover {
    @extend .option-active;
  }

  &-active {
    // background: darken($ui-white, 10%); --- https://sass-lang.com/documentation/breaking-changes/color-functions/
  }

  .option-text {
    display: flex;
    justify-items: center;
    gap: 6px;

    span {
      display: flex;
      align-items: center;
    }

    svg {
      filter: invert(16%) sepia(93%) saturate(7371%) hue-rotate(1deg)
        brightness(108%) contrast(116%);
    }
  }
}

.postfix {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  &-text {
    margin: 0;
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;
    color: $typography-secondary-color;
    max-width: 164px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}

.note {
  @include typography-secondary;
  white-space: pre-line;
  text-align: left;
  background: transparent;
}

.current-option {
  @include row(25px);
  justify-content: space-between;
  width: 100%;
  color: $typography-paragraph;

  .search-input {
    width: 100%;
    outline: none;
    border: 0;
    font-size: 16px;
    line-height: 24px;
    color: $typography-paragraph;
    flex-grow: 1;
    text-align: left;
  }

  &__text {
    flex-grow: 1;
    text-align: left;
  }

  &__placeholder {
    @include typography-placeholder;
  }
}

.chevron-active {
  transform: rotate(180deg);
}
</style>
